import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Activity, CheckCircle2, AlertTriangle, XCircle, Server, Database,
  Brain, Mail, Coins, Clock, ChevronRight, ExternalLink, Zap, Shield, Globe2
} from 'lucide-react';
import FooterBackground from '../components/FooterBackground';
import PublicNavbar from '../components/PublicNavbar';
import NewsletterSignup from '../components/NewsletterSignup';
import SEOHead from '../components/SEOHead';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  operational: {
    label: 'Operational',
    color: 'emerald',
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-400',
    border: 'border-emerald-500/30',
    dot: 'bg-emerald-500',
  },
  degraded: {
    label: 'Degraded',
    color: 'amber',
    bg: 'bg-amber-500/10',
    text: 'text-amber-400',
    border: 'border-amber-500/30',
    dot: 'bg-amber-500',
  },
  down: {
    label: 'Down',
    color: 'rose',
    bg: 'bg-rose-500/10',
    text: 'text-rose-400',
    border: 'border-rose-500/30',
    dot: 'bg-rose-500',
  },
  unknown: {
    label: 'Unknown',
    color: 'slate',
    bg: 'bg-slate-500/10',
    text: 'text-slate-400',
    border: 'border-slate-500/30',
    dot: 'bg-slate-500',
  },
};

const OVERALL_META = {
  operational: { label: 'All systems operational', tone: 'emerald', headline: 'All systems operational' },
  degraded_performance: { label: 'Degraded performance', tone: 'amber', headline: 'Degraded performance' },
  partial_outage: { label: 'Partial outage', tone: 'amber', headline: 'Partial outage' },
  major_outage: { label: 'Major outage', tone: 'rose', headline: 'Major outage' },
};

const COMPONENT_ICONS = {
  // Generic public-facing labels — backend (Iter84d) anonymizes infra names.
  // DO NOT add vendor names ("MongoDB", "Anthropic", "OpenAI") to this map —
  // it would force the backend to send those names to render the icon, defeating
  // the whole privacy-safe point. Anything not in this map renders Globe2.
  'API Server': Server,
  'Database': Database,
  'AI Providers': Brain,
  'Email Service': Mail,
  'Currency / FX': Coins,
  // v34.93 — future generic categories (add as backend rolls them up):
  'Web App': Globe2,
  'Slack Bot': Zap,
  'CLI': Server,
  'Payments': Shield,
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.unknown;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${m.bg} ${m.text} border ${m.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot} ${status === 'operational' ? 'animate-pulse' : ''}`} />
      {m.label}
    </span>
  );
};

const ComponentRow = ({ component }) => {
  const Icon = COMPONENT_ICONS[component.name] || Globe2;
  const m = STATUS_META[component.status] || STATUS_META.unknown;
  return (
    <div
      className={`flex items-center justify-between gap-3 px-4 py-3.5 border-b border-white/5 last:border-0 transition-colors hover:bg-white/[0.02]`}
      data-testid={`status-row-${component.name.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`p-2 rounded-lg ${m.bg}`}>
          <Icon className={`w-4 h-4 ${m.text}`} />
        </div>
        <div className="min-w-0">
          <div className="font-medium text-slate-900 truncate">{component.name}</div>
          <div className="text-xs text-slate-400">{component.detail || '—'}</div>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {component.latency_ms != null && (
          <span className="text-xs font-mono text-slate-400 hidden sm:inline">{component.latency_ms}ms</span>
        )}
        <StatusBadge status={component.status} />
      </div>
    </div>
  );
};

const UptimeBar = ({ days }) => {
  if (!days || !days.length) return null;
  return (
    <div className="flex items-center gap-[2px] h-8" data-testid="uptime-bar">
      {days.map((d) => {
        const tone =
          d.uptime_pct >= 99.9 ? 'bg-emerald-500/80'
          : d.uptime_pct >= 95 ? 'bg-amber-500/80'
          : 'bg-rose-500/80';
        return (
          <div
            key={d.date}
            className={`flex-1 ${tone} rounded-sm hover:scale-y-110 origin-bottom transition-transform`}
            title={`${d.date} — ${d.uptime_pct.toFixed(2)}% uptime`}
          />
        );
      })}
    </div>
  );
};

const StatusPage = () => {
  const [status, setStatus] = useState(null);
  const [uptime, setUptime] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const [s, u, i] = await Promise.all([
        axios.get(`${API_URL}/status/components`),
        axios.get(`${API_URL}/status/uptime`),
        axios.get(`${API_URL}/status/incidents?limit=10`),
      ]);
      setStatus(s.data);
      setUptime(u.data);
      setIncidents(i.data?.incidents || []);
      setLastChecked(new Date());
    } catch (err) {
      console.error('Failed to load status', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const id = setInterval(() => fetchAll(true), 60_000); // auto-refresh every 60s
    return () => clearInterval(id);
  }, [fetchAll]);

  const overall = status?.overall || 'operational';
  const overallMeta = OVERALL_META[overall] || OVERALL_META.operational;
  const toneCls = {
    emerald: 'from-emerald-500 to-cyan-500',
    amber: 'from-amber-500 to-orange-500',
    rose: 'from-rose-500 to-red-500',
  }[overallMeta.tone];
  const toneText = {
    emerald: 'text-emerald-400',
    amber: 'text-amber-400',
    rose: 'text-rose-400',
  }[overallMeta.tone];

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100" data-testid="status-page">
      <SEOHead
        title="System Status — CARTIEAI"
        description="Real-time health of every CARTIEAI service. Live uptime, component status, latency, and incident history. Auto-refreshes every 60 seconds."
        canonical="https://cartieai.com/status"
        ogImage="https://cartieai.com/og-status.png"
      />
      <PublicNavbar />

      {/* Hero */}
      <section className="relative px-4 pt-24 pb-16 overflow-hidden">
        <FooterBackground />
        <div className="relative max-w-5xl mx-auto">
          <div className="flex items-center gap-2 mb-4 text-xs font-bold tracking-widest text-slate-400 uppercase">
            <Activity className="w-4 h-4 text-emerald-400" />
            <span>System Status</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
            <span className={`bg-clip-text text-transparent bg-gradient-to-r ${toneCls}`}>
              {overallMeta.headline}
            </span>
          </h1>
          <p className="text-slate-400 max-w-2xl">
            Real-time health of every CARTIEAI service. Auto-refreshes every 60 seconds.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-5 text-xs text-slate-400">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
              <span className={`w-2 h-2 rounded-full bg-${overallMeta.tone}-500 animate-pulse`} />
              <span className={toneText}>{overallMeta.label}</span>
            </span>
            {lastChecked && (
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                Last checked {lastChecked.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={() => fetchAll()}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 transition-colors disabled:opacity-60"
              data-testid="status-refresh-btn"
            >
              <Zap className="w-3.5 h-3.5" />
              {refreshing ? 'Refreshing…' : 'Refresh now'}
            </button>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 pb-24 space-y-10">
        {/* Component matrix */}
        <section data-testid="components-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold">Components</h2>
            <span className="text-xs text-slate-400">{(status?.components || []).length} services monitored</span>
          </div>
          <div className="rounded-2xl border border-white/10 bg-slate-900/50 backdrop-blur-sm overflow-hidden">
            {loading && !status ? (
              <div className="px-6 py-12 text-center text-slate-400">Probing services…</div>
            ) : (
              (status?.components || []).map((c) => <ComponentRow key={c.name} component={c} />)
            )}
          </div>
        </section>

        {/* 90-day uptime */}
        <section data-testid="uptime-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold">90-Day Uptime</h2>
            {uptime && (
              <span className="text-sm text-emerald-400 font-mono font-bold" data-testid="overall-uptime">
                {uptime.overall_uptime_pct.toFixed(3)}%
              </span>
            )}
          </div>
          <div className="rounded-2xl border border-white/10 bg-slate-900/50 backdrop-blur-sm p-5">
            {uptime ? (
              <>
                <UptimeBar days={uptime.days} />
                <div className="flex items-center justify-between text-xs text-slate-400 mt-3">
                  <span>{uptime.days[0]?.date}</span>
                  <span className="hidden sm:inline">Higher bars = better uptime · hover any day for details</span>
                  <span>Today</span>
                </div>
              </>
            ) : (
              <div className="text-sm text-slate-400">Loading uptime…</div>
            )}
          </div>
        </section>

        {/* Recent incidents */}
        <section data-testid="incidents-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold">Recent Incidents</h2>
            <span className="text-xs text-slate-400">Past 90 days</span>
          </div>
          {incidents.length === 0 ? (
            <div className="rounded-2xl border border-emerald-500/15 bg-gradient-to-br from-emerald-500/[0.05] to-transparent p-8 text-center">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-emerald-400" />
              <p className="font-bold text-slate-900 mb-1">No incidents reported</p>
              <p className="text-sm text-slate-400">All systems have been running smoothly. We'll post updates here if anything happens.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {incidents.map((inc, idx) => {
                const sev = (inc.severity || 'info').toLowerCase();
                const isResolved = (inc.status || '').toLowerCase() === 'resolved';
                const SevIcon = sev === 'critical' || sev === 'major' ? XCircle
                  : sev === 'warning' || sev === 'minor' ? AlertTriangle : CheckCircle2;
                const sevTone = isResolved ? 'text-emerald-400'
                  : sev === 'critical' ? 'text-rose-400'
                  : sev === 'warning' || sev === 'minor' ? 'text-amber-400' : 'text-emerald-400';
                const startedDate = inc.started_at ? new Date(inc.started_at) : null;
                const endedDate = inc.ended_at ? new Date(inc.ended_at) : null;
                const durationMin = (startedDate && endedDate)
                  ? Math.max(1, Math.round((endedDate - startedDate) / 60000))
                  : null;
                return (
                  <div
                    key={inc.id || idx}
                    className="rounded-xl border border-white/10 bg-slate-900/50 p-5"
                    data-testid={`incident-${idx}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      {isResolved ? (
                        <CheckCircle2 className="w-5 h-5 mt-0.5 text-emerald-400" />
                      ) : (
                        <SevIcon className={`w-5 h-5 mt-0.5 ${sevTone}`} />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-bold text-slate-900">{inc.title || 'Incident'}</span>
                          {isResolved && (
                            <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-bold tracking-widest">RESOLVED</span>
                          )}
                          {sev && (
                            <span className={`text-[11px] px-1.5 py-0.5 rounded-full border font-bold tracking-wider ${
                              sev === 'critical' ? 'bg-rose-500/15 text-rose-300 border-rose-500/30'
                              : sev === 'warning' || sev === 'minor' ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                              : 'bg-slate-500/15 text-slate-300 border-slate-500/30'
                            }`}>{sev.toUpperCase()}</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-400 leading-relaxed">{inc.summary || ''}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-400">
                          {startedDate && (
                            <span>Started: <span className="font-mono text-slate-400">{startedDate.toLocaleString()}</span></span>
                          )}
                          {endedDate && (
                            <span>Resolved: <span className="font-mono text-slate-400">{endedDate.toLocaleString()}</span></span>
                          )}
                          {durationMin && (
                            <span>Duration: <span className="font-mono text-slate-400">{durationMin} min</span></span>
                          )}
                          {(inc.components_affected || []).length > 0 && (
                            <span>Affected: <span className="font-mono text-slate-400">{inc.components_affected.join(', ')}</span></span>
                          )}
                        </div>
                      </div>
                    </div>
                    {(inc.updates || []).length > 0 && (
                      <div className="ml-8 pl-4 border-l border-white/10 space-y-2.5 mt-3">
                        {inc.updates.map((u, uidx) => {
                          const at = u.at ? new Date(u.at) : null;
                          const ustatus = (u.status || '').replace(/_/g, ' ');
                          return (
                            <div key={uidx} className="text-xs">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-emerald-400 font-bold uppercase tracking-wider">{ustatus}</span>
                                {at && <span className="text-slate-400 font-mono">{at.toLocaleString()}</span>}
                              </div>
                              <p className="text-slate-400 leading-relaxed">{u.message}</p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {inc.post_mortem_url && (
                      <a
                        href={inc.post_mortem_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 mt-3 ml-8 text-xs font-semibold text-emerald-400 hover:text-emerald-300"
                        data-testid={`incident-postmortem-${idx}`}
                      >
                        Read full post-mortem
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Trust signals */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-5">
            <Shield className="w-5 h-5 text-emerald-400 mb-2" />
            <p className="font-semibold text-slate-900">Probes every 60s</p>
            <p className="text-xs text-slate-400 mt-1">Real HTTP checks against every upstream — not cached marketing.</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-5">
            <Activity className="w-5 h-5 text-emerald-400 mb-2" />
            <p className="font-semibold text-slate-900">SOC 2-aligned monitoring</p>
            <p className="text-xs text-slate-400 mt-1">Audit logs, anomaly detection, on-call escalation paths in place.</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-5">
            <Globe2 className="w-5 h-5 text-emerald-400 mb-2" />
            <p className="font-semibold text-slate-900">Multi-region failover</p>
            <p className="text-xs text-slate-400 mt-1">Database, AI providers, and outbound webhooks all redundant.</p>
          </div>
        </section>

        {/* Subscribe + back link */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-6">
            <p className="text-xs font-bold tracking-widest text-slate-400 uppercase mb-2">Get notified</p>
            <h3 className="text-xl font-bold mb-2">Subscribe to status updates</h3>
            <p className="text-sm text-slate-400 mb-4">We'll email you the moment a component is degraded — and again the moment it's back to operational.</p>
            <NewsletterSignup source="status_page" />
          </div>
          <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-6 flex flex-col justify-between">
            <div>
              <p className="text-xs font-bold tracking-widest text-slate-400 uppercase mb-2">Need help?</p>
              <h3 className="text-xl font-bold mb-2">Contact our team</h3>
              <p className="text-sm text-slate-400 mb-4">If something looks wrong here that isn't already reported, please reach out.</p>
            </div>
            <div className="space-y-2">
              <a
                href="mailto:hello@cartieai.com"
                className="flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 transition-colors text-sm font-medium"
                data-testid="status-contact-email"
              >
                <span className="flex items-center gap-2"><Mail className="w-4 h-4" /> hello@cartieai.com</span>
                <ExternalLink className="w-4 h-4" />
              </a>
              <Link
                to="/"
                className="flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-slate-600 border border-white/10 transition-colors text-sm font-medium"
                data-testid="status-back-home"
              >
                <span>Back to CARTIEAI</span>
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </div>

      {/* Footer band — keeps the same neural feel */}
      <div className="relative">
        <FooterBackground />
        <footer className="relative py-10 px-4 border-t border-white/5">
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
            <span>&copy; 2026 CARTIEAI · Status page powered by live probes</span>
            <span className="flex items-center gap-2">
              <span className={`w-1.5 h-1.5 rounded-full bg-${overallMeta.tone}-500 animate-pulse`} />
              {overallMeta.label}
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default StatusPage;
